import { Modal, Form, Select, DatePicker, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import type { Audit } from '../../../types/audit';
import type { AppUser } from '../../../types';
import dayjs from 'dayjs';

interface AssignAuditorModalProps {
  visible: boolean;
  onClose: () => void;
  audit: Audit | null;
  auditors: AppUser[];
  // Used to filter the dropdown to auditors that actually work this
  // planning: same audit company AND same portfolio/country.
  auditCompanyId?: number | null;
  countryId?: number | null;
  onSuccess?: () => void;
}

export const AssignAuditorModal: React.FC<AssignAuditorModalProps> = ({
  visible,
  onClose,
  audit,
  auditors,
  auditCompanyId,
  countryId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  // Internal list — we fetch fresh from /app-users so the Select can
  // resolve the current `auditor_user` id to a name even when the
  // planning hasn't been associated with that auditor yet (which is
  // exactly the case the coordinator hits the most).
  const [loadedAuditors, setLoadedAuditors] = useState<AppUser[] | null>(null);
  const [loadingAuditors, setLoadingAuditors] = useState(false);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    (async () => {
      setLoadingAuditors(true);
      try {
        const { apiClient } = await import('../../../services/api/apiClient');
        // Restrict to the planning's audit company and country so the
        // dropdown only lists auditors whose portfolio matches.
        const params = new URLSearchParams({ page_size: '500' });
        if (auditCompanyId != null) {
          params.set('audit_company', String(auditCompanyId));
        }
        if (countryId != null) {
          params.set('country', String(countryId));
        }
        const response = await apiClient.get(
          `/app-users/?${params.toString()}`,
        );
        const list =
          response.data?.results ?? response.data?.data ?? response.data ?? [];
        if (!cancelled) {
          setLoadedAuditors(Array.isArray(list) ? list : []);
        }
      } catch (err) {
        if (!cancelled) setLoadedAuditors([]);
      } finally {
        if (!cancelled) setLoadingAuditors(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, auditCompanyId, countryId]);

  // Use the live list if loaded; fall back to the prop so the modal
  // still works on first paint before the fetch resolves. We dedupe
  // by id to avoid duplicate option entries when both sources have
  // the same auditor. We also seed the list with the audit's currently
  // assigned auditor (using its denormalized `auditor_name`) — without
  // this, when the current auditor is outside the audit_company/country
  // filter the Select can't resolve the value and renders the raw ID.
  const auditorOptions = (() => {
    const map = new Map<number, AppUser>();
    for (const a of auditors || []) map.set(a.id, a);
    for (const a of loadedAuditors || []) map.set(a.id, a);
    const currentId =
      typeof audit?.auditor_user === 'object'
        ? (audit?.auditor_user as any)?.id
        : (audit?.auditor_user as number | null | undefined);
    if (currentId != null && !map.has(currentId)) {
      map.set(currentId, {
        id: currentId,
        name: (audit as any)?.auditor_name || `#${currentId}`,
      } as AppUser);
    }
    return Array.from(map.values()).sort((a, b) =>
      (a.name || '').localeCompare(b.name || ''),
    );
  })();

  // Set initial values when audit changes
  const handleOpen = () => {
    console.log('🔍 AssignAuditorModal opened with audit:', {
      id: audit?.id,
      auditor_user: audit?.auditor_user,
      auditor_name: audit?.auditor_name,
      planning_date: audit?.planning_date,
    });
    console.log('🔍 Available auditors:', auditors?.length);
    
    if (audit) {
      form.setFieldsValue({
        auditor_user: audit.auditor_user || undefined,
        planning_date: audit.planning_date ? dayjs(audit.planning_date) : undefined,
      });
    }
  };

  const handleSave = async (values: any) => {
    if (!audit) return;

    console.log('💾 Saving audit assignment:', {
      auditId: audit.id,
      values,
      auditor_user: values.auditor_user,
      planning_date: values.planning_date?.format('YYYY-MM-DD'),
    });

    setSaving(true);
    try {
      const { apiClient } = await import('../../../services/api/apiClient');

      // El modal edita dos campos independientes: auditor y fecha
      // planificada. Ambos son opcionales — enviar `null` explícito
      // desasigna (auditor sin auditor) o limpia la fecha. Antes se
      // enviaba SOLO cuando el valor era truthy → no se podía
      // desasignar desde web (el `undefined` no llegaba al PATCH).
      // Ahora enviamos `undefined → null` para que el backend sepa
      // que hay que limpiar el campo.
      // Sentinel 0 (opción "Sin asignar") también se traduce a null.
      // Cubre ambos gestos: allowClear (undefined) y selección
      // explícita de la opción sintética.
      const rawAuditorUser = values.auditor_user;
      const normalisedAuditorUser =
        typeof rawAuditorUser === 'number' && rawAuditorUser > 0
          ? rawAuditorUser
          : null;
      const updateData: {
        auditor_user?: number | null;
        planning_date?: string | null;
      } = {
        auditor_user: normalisedAuditorUser,
        planning_date: values.planning_date
          ? values.planning_date.format('YYYY-MM-DD')
          : null,
      };

      console.log('📤 Sending PATCH request:', {
        url: `/audits/${audit.id}/`,
        data: updateData,
      });

      const response = await apiClient.patch(`/audits/${audit.id}/`, updateData);
      
      console.log('✅ PATCH response:', response.data);
      
      message.success(t('audit.assign_success') || 'Auditor y fecha asignados correctamente');
      form.resetFields();
      onClose();
      
      console.log('🔄 Calling onSuccess callback');
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('❌ Error assigning auditor:', error);
      const errorMessage = error.originalError?.response?.data?.error || 
                          error.message || 
                          t('audit.assign_error') || 'Error al asignar auditor';
      message.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={t('audit.assign_auditor') || 'Asignar Auditor y Fecha'}
      open={visible}
      onOk={() => form.submit()}
      onCancel={handleClose}
      confirmLoading={saving}
      okText={t('common.save') || 'Guardar'}
      cancelText={t('common.cancel') || 'Cancelar'}
      afterOpenChange={(open) => {
        if (open) {
          handleOpen();
        }
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
      >
        <Form.Item
          label={t('audit.auditor') || 'Auditor'}
          name="auditor_user"
          extra={
            t(
              'audit.auditor_clear_help',
              'Déjalo vacío para dejar la auditoría sin auditor asignado.',
            )
          }
        >
          <Select
            placeholder={
              t('audit.select_auditor_or_clear', 'Sin auditor asignado')
            }
            showSearch
            allowClear
            loading={loadingAuditors}
            optionFilterProp="label"
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
            dropdownStyle={{ zIndex: 10000 }}
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
            // Primera opción sintética "Sin asignar" (value = 0) que
            // el usuario puede elegir explícitamente para desasignar
            // el auditor. La X del allowClear se conserva por si
            // prefiere ese gesto — ambas rutas acaban en null en el
            // handleSave. AppUser.id es siempre > 0 así que 0 es un
            // sentinel seguro.
            options={[
              {
                value: 0,
                label: (
                  <span style={{ color: "#8c8c8c", fontStyle: "italic" }}>
                    — {t("planning.unassigned_auditor", "Sin asignar")} —
                  </span>
                ),
              },
              ...auditorOptions.map((auditor) => ({
                label: (auditor as any).code
                  ? `${auditor.name} (${(auditor as any).code})`
                  : auditor.name,
                value: auditor.id,
              })),
            ]}
          />
        </Form.Item>

        <Form.Item
          label={t('audit.planning_date') || 'Fecha Planificada'}
          name="planning_date"
        >
          <DatePicker
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            placeholder={t('audit.select_date') || 'Seleccione la fecha'}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
